import React, { useState, useEffect, useRef, useCallback } from 'react';
import ReactGA from 'react-ga4';
import { createWorker } from 'tesseract.js';
import { useAlbums } from '../context/AlbumsContext';
import './ScannerSticker.css';

function parseCode(rawText, validSiglas) {
  const normalized = rawText.toUpperCase().replace(/\s+/g, ' ').trim();
  // Exige separador (início de string, espaço, ou não-letra) antes da sigla
  // para evitar matches dentro de palavras como "WORLDCUP".
  const regex = /(?:^|[^A-Z])([A-Z]{2,4})\s?(\d{1,2})(?![0-9A-Z])/g;
  let match;
  while ((match = regex.exec(normalized)) !== null) {
    const sigla = match[1];
    if (validSiglas && !validSiglas.has(sigla)) continue;
    const num = String(parseInt(match[2], 10)).padStart(2, '0');
    return { sigla, key: `${sigla}_${num}` };
  }
  return null;
}

function findSticker(stickers, sigla, key) {
  if (stickers[sigla]?.jogadores[key])
    return { pais: stickers[sigla], jogador: stickers[sigla].jogadores[key], key };
  if (stickers['EXTRAS']?.jogadores[key])
    return { pais: stickers['EXTRAS'], jogador: stickers['EXTRAS'].jogadores[key], key };
  return null;
}

const ScannerSticker = ({ onNavigate }) => {
  const { stickers, activeAlbumId, toggleFigurinha } = useAlbums();

  const [phase, setPhase] = useState('idle');
  const [scanResult, setScanResult] = useState(null);
  const [ocrError, setOcrError] = useState(null);
  const [manualInput, setManualInput] = useState('');
  const [showManual, setShowManual] = useState(false);
  const [marked, setMarked] = useState(false);
  const [isWorkerReady, setIsWorkerReady] = useState(false);
  const [isCameraActive, setIsCameraActive] = useState(false);
  const [showOnboarding, setShowOnboarding] = useState(() => {
    return localStorage.getItem('scannerOnboardingSeen') !== '1';
  });

  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const workerRef = useRef(null);
  const scanLoopRef = useRef(null);
  const lastScanTimeRef = useRef(0);
  const ocrBusyRef = useRef(false);

  useEffect(() => {
    let worker;
    (async () => {
      worker = await createWorker('eng');
      await worker.setParameters({
        tessedit_char_whitelist: 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789 ',
        // PSM 7: tratar imagem como uma única linha de texto — muito mais rápido que o default
        tessedit_pageseg_mode: '7',
        // Desativar saídas que não usamos para acelerar
        tessjs_create_hocr: '0',
        tessjs_create_tsv: '0',
        tessjs_create_box: '0',
        tessjs_create_unlv: '0',
        tessjs_create_osd: '0',
      });
      workerRef.current = worker;
      setIsWorkerReady(true);
    })();
    return () => { worker?.terminate(); };
  }, []);

  const SCAN_INTERVAL_MS = 350;

  const startScanLoop = useCallback(() => {
    const validSiglas = new Set(Object.keys(stickers || {}));
    // console.log('[OCR] scan loop started. valid siglas:', [...validSiglas]);
    const loop = async (timestamp) => {
      scanLoopRef.current = requestAnimationFrame(loop);
      if (timestamp - lastScanTimeRef.current < SCAN_INTERVAL_MS) return;
      if (ocrBusyRef.current) return;
      if (!workerRef.current || !videoRef.current || !canvasRef.current) return;
      if (videoRef.current.readyState < 2) return;

      lastScanTimeRef.current = timestamp;
      ocrBusyRef.current = true;
      const video = videoRef.current;
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d', { willReadFrequently: true });
      const vw = video.videoWidth;
      const vh = video.videoHeight;
      if (vw === 0 || vh === 0) return;

      // Compensar object-fit: cover — descobrir a área do vídeo realmente visível na tela
      const displayW = video.clientWidth;
      const displayH = video.clientHeight;
      const videoAspect = vw / vh;
      const displayAspect = displayW / displayH;
      let visibleW, visibleH, visibleX, visibleY;
      if (videoAspect > displayAspect) {
        // vídeo é mais largo que a tela — cortado nas laterais
        visibleH = vh;
        visibleW = vh * displayAspect;
        visibleX = (vw - visibleW) / 2;
        visibleY = 0;
      } else {
        // vídeo é mais alto que a tela — cortado no topo/baixo
        visibleW = vw;
        visibleH = vw / displayAspect;
        visibleX = 0;
        visibleY = (vh - visibleH) / 2;
      }

      // Crop alinhado com a guia verde (CSS): top 21%, right 16%, width 22%, height 6%
      // Adiciona uma margem (~3%) ao redor para tolerar tremor do enquadramento
      const cropX = Math.floor(visibleX + visibleW * 0.59);
      const cropY = Math.floor(visibleY + visibleH * 0.19);
      const cropW = Math.floor(visibleW * 0.28);
      const cropH = Math.floor(visibleH * 0.10);

      // Upscale 3x para o OCR — texto pequeno fica legível
      const outW = cropW * 3;
      const outH = cropH * 3;
      canvas.width = outW;
      canvas.height = outH;
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';
      ctx.drawImage(video, cropX, cropY, cropW, cropH, 0, 0, outW, outH);

      // Threshold (binarização) — converte para preto/branco com base no brilho médio
      const imageData = ctx.getImageData(0, 0, outW, outH);
      const data = imageData.data;
      let sum = 0;
      for (let i = 0; i < data.length; i += 4) {
        sum += (data[i] + data[i + 1] + data[i + 2]) / 3;
      }
      const avgBrightness = sum / (data.length / 4);
      const threshold = avgBrightness * 0.85;
      for (let i = 0; i < data.length; i += 4) {
        const lum = (data[i] + data[i + 1] + data[i + 2]) / 3;
        const v = lum < threshold ? 0 : 255;
        data[i] = v;
        data[i + 1] = v;
        data[i + 2] = v;
      }
      ctx.putImageData(imageData, 0, 0);

      try {
        const t0 = performance.now();
        const { data: ocrData } = await workerRef.current.recognize(canvas);
        const tMs = Math.round(performance.now() - t0);
        const text = ocrData.text.replace(/\n/g, ' \\n ').trim();
        // console.log('[OCR]', { ms: tMs, confidence: Math.round(ocrData.confidence), text });
        const parsed = parseCode(ocrData.text, validSiglas);
        // console.log('[OCR] parsed:', parsed);
        if (parsed) {
          const found = findSticker(stickers, parsed.sigla, parsed.key);
          // console.log('[OCR] found:', !!found);
          if (found) {
            cancelAnimationFrame(scanLoopRef.current);
            setScanResult({ ...parsed, ...found });
            setMarked(false);
            ReactGA.event({ category: 'Scanner', action: 'Sticker Scanned', label: parsed.key });
          }
        }
      } catch (err) {
        console.warn('[OCR] error:', err);
      } finally {
        ocrBusyRef.current = false;
      }
    };
    scanLoopRef.current = requestAnimationFrame(loop);
  }, [stickers]);

  const stopCamera = useCallback(() => {
    if (scanLoopRef.current) cancelAnimationFrame(scanLoopRef.current);
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    setIsCameraActive(false);
  }, []);

  useEffect(() => () => stopCamera(), [stopCamera]);

  const startCamera = useCallback(async () => {
    try {
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        throw new Error('Câmera não suportada neste navegador');
      }

      // Warn if not HTTPS (except localhost)
      if (window.location.protocol !== 'https:' && !window.location.hostname.includes('localhost') && window.location.hostname !== '127.0.0.1') {
        console.warn('⚠️ Câmera requer HTTPS. Acesso pode ser bloqueado.');
      }

      // Try with facingMode first, fallback to basic video constraint
      let stream;
      try {
        stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: { ideal: 'environment' } },
          audio: false
        });
      } catch (firstErr) {
        // console.warn('Failed with facingMode constraint, trying basic video:', firstErr.message);
        stream = await navigator.mediaDevices.getUserMedia({
          video: true,
          audio: false
        });
      }
      streamRef.current = stream;
      videoRef.current.srcObject = stream;

      // Wait for video to have dimensions
      await new Promise(resolve => {
        const onLoadedMetadata = () => {
          videoRef.current.removeEventListener('loadedmetadata', onLoadedMetadata);
          resolve();
        };
        videoRef.current.addEventListener('loadedmetadata', onLoadedMetadata);
        setTimeout(resolve, 1000); // fallback timeout
      });

      await videoRef.current.play();
      setIsCameraActive(true);
      setPhase('scanning');
      startScanLoop();
    } catch (err) {
      console.error('Camera error:', err.name, err.message);
      let errorMsg = 'Não foi possível acessar a câmera';

      if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
        errorMsg = 'Permissão de câmera negada.\n\n👉 Verifique:\n1. Abra as configurações do seu navegador\n2. Procure por "Câmera" ou "Permissões"\n3. Certifique-se que a câmera está ativada para este site';
      } else if (err.name === 'NotFoundError' || err.name === 'DevicesNotFoundError') {
        errorMsg = 'Câmera não encontrada no dispositivo.';
      } else if (err.name === 'NotSupportedError') {
        errorMsg = 'Câmera não suportada neste navegador. Tente um navegador mais recente.';
      } else if (err.message?.includes('Permission denied')) {
        errorMsg = 'Permissão de câmera negada. Abra as configurações do seu navegador e ative a câmera.';
      } else if (err.message) {
        errorMsg = `Erro: ${err.message}`;
      }

      setOcrError(errorMsg);
      setPhase('error');
    }
  }, [startScanLoop]);

  // Iniciar câmera quando worker estiver pronto e onboarding fechado
  useEffect(() => {
    if (isWorkerReady && !isCameraActive && !showOnboarding) {
      startCamera();
    }
  }, [isWorkerReady, isCameraActive, showOnboarding, startCamera]);

  const handleCloseOnboarding = () => {
    localStorage.setItem('scannerOnboardingSeen', '1');
    setShowOnboarding(false);
  };

  const handleManualSubmit = () => {
    const parsed = parseCode(manualInput, new Set(Object.keys(stickers || {})));
    if (!parsed) { setOcrError('Código inválido. Use o formato: BRA 19'); return; }
    const found = findSticker(stickers, parsed.sigla, parsed.key);
    if (!found) { setOcrError(`Figurinha "${parsed.key}" não encontrada.`); return; }
    setScanResult({ ...parsed, ...found });
    setMarked(false);
  };

  const handleMarcar = () => {
    if (!scanResult) return;
    toggleFigurinha(activeAlbumId, scanResult.sigla, scanResult.key);
    setMarked(true);
    ReactGA.event({ category: 'Scanner', action: 'Sticker Marked Via Scanner', label: scanResult.key });
  };

  const handleScanNext = () => {
    setScanResult(null);
    setMarked(false);
    setManualInput('');
    setOcrError(null);
    startScanLoop();
  };

  const liveObtida = scanResult
    ? stickers?.[scanResult.sigla]?.jogadores?.[scanResult.key]?.obtida ?? scanResult.jogador.obtida
    : false;

  if (showOnboarding) {
    return (
      <div className="scanner-page">
        <div className="scanner-onboarding">
          <h2 className="scanner-onboarding-title">Como escanear</h2>
          <p className="scanner-onboarding-text">
            Aponte para o <strong>verso</strong> da figurinha e enquadre o código no canto superior direito.
          </p>
          <div className="scanner-onboarding-image-wrap">
            <img
              src={`${process.env.PUBLIC_URL}/scanner-exemplo.jpg`}
              alt="Exemplo: verso da figurinha com o código BRA 19 destacado"
              className="scanner-onboarding-image"
            />
          </div>
          <button className="btn-primario scanner-onboarding-btn" onClick={handleCloseOnboarding}>
            Entendi, escanear agora
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="scanner-page">
      <main className="scanner-main">
        <div className="camera-container">
          {isCameraActive && <p className="scanner-instruction">Enquadre a figurinha dentro do retângulo amarelo e o código da figurinha no retângulo verde</p>}
          <video ref={videoRef} className="camera-video" playsInline muted autoPlay />
          <canvas ref={canvasRef} className="scanner-canvas-hidden" />
          <div className="scanner-overlay">
            <div className="scanner-overlay-guide" />
          </div>
          {!isCameraActive && phase === 'idle' && (
            <div className="camera-start-prompt">
              <button className="btn-primario" onClick={startCamera} disabled={!isWorkerReady}>
                {isWorkerReady ? '📷 Iniciar Scanner' : 'Carregando OCR...'}
              </button>
            </div>
          )}
          {phase === 'error' && <p className="scanner-error">{ocrError}</p>}
        </div>

        {isCameraActive && !scanResult && (
          <div className="manual-fallback">
            <button className="btn-link" onClick={() => setShowManual(v => !v)}>
              {showManual ? 'Usar câmera' : 'Digitar manualmente'}
            </button>
            {showManual && (
              <div className="manual-input-row">
                <input
                  type="text" placeholder="Ex: BRA 19" value={manualInput} maxLength={8}
                  onChange={(e) => setManualInput(e.target.value.toUpperCase())}
                />
                <button className="btn-primario" onClick={handleManualSubmit}>OK</button>
                {ocrError && <p className="form-error">{ocrError}</p>}
              </div>
            )}
          </div>
        )}
      </main>

      {scanResult && (
        <div className="scanner-modal-backdrop">
          <div className="scanner-modal-sheet">
            <div className="scanner-modal-header">
              <span className="scanner-modal-code">
                {scanResult.sigla} {parseInt(scanResult.key.split('_')[1], 10)}
              </span>
              <span className="scanner-modal-flag">{scanResult.pais.flag}</span>
            </div>
            <p className="scanner-modal-player">{scanResult.jogador.nome}</p>
            <p className="scanner-modal-country">{scanResult.pais.nome}</p>
            <div className="scanner-modal-status">
              {liveObtida ? (
                <span className="scanner-badge-obtida">✓ Você já tem essa figurinha</span>
              ) : (
                <span className="scanner-badge-faltando">📌 Você não tem essa figurinha</span>
              )}
            </div>
            {!liveObtida && !marked && <button className="btn-primario scanner-btn-marcar" onClick={handleMarcar}>Marcar como obtida</button>}
            {marked && <p className="scanner-success">Figurinha marcada! ✓</p>}
            <div className="scanner-modal-footer">
              <button className="btn-secundario" onClick={handleScanNext}>Escanear outra</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ScannerSticker;
